import {CLStore} from 'common/src/redux/store';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {CreativeCardScratchpadState} from '../../scratchpad/creativeCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {Creative, CreativeNodeData} from 'common/src/modules/creatives/creatives.types';
import getCreativeForCampaign from '../../utils/creative/getCreativeForCampaign';
import validateCreativeToBeUsedAsFallbackCreative from '../../utils/creative/validateCreativeToBeUsedAsFallbackCreative';

const setCreativeInputSchema = z.object({
  creativeId: z.number().describe('The ID of the creative to set on the card'),
});

function toCreativeNodeData(creative: Creative): CreativeNodeData {
  return {
    id: creative.id,
    name: creative.name,
    poster: creative.poster,
    productType: creative.productType.value,
    type: creative.type.value,
    features: creative.features,
    staticCreativeData: creative.staticCreativeData,
    template: creative.template,
    formats: creative.formats.map((format) => ({
      width: format.width,
      height: format.height,
      minItemCount: format.minItemCount,
      maxItemCount: format.maxItemCount,
      videoProp: format.videoProp,
      issues: format.issues,
      verificationResult: format.verificationResult,
      rendererVersion: format.RendererVersion,
      isAvailable: true,
    })),
    issues: creative.issues,
    tag1Value: creative.tag1Value,
    tag2Value: creative.tag2Value,
    tag3Value: creative.tag3Value,
  };
}

function setCreativeTool(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<typeof setCreativeInputSchema, CreativeCardScratchpadState>,
  ) {
    const {creativeId} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<CreativeCardScratchpadState>(
      agentScratchpad,
      NodeType.CREATIVE,
    );

    const cardId = validatedScratchpad.card.id;
    const isFallback = validatedScratchpad.card.isFallbackCreative ?? false;

    const result = await getCreativeForCampaign(store, creativeId, cardId, isFallback);

    if (result.found === false) {
      return toolResult(result.errorMessage);
    }

    const {creative} = result;

    if (isFallback) {
      const fallbackValidation = validateCreativeToBeUsedAsFallbackCreative(creative);

      if (fallbackValidation.isValid === false) {
        return toolResult(fallbackValidation.errorMessage);
      }
    }

    validatedScratchpad.card.creativeId = creativeId;
    validatedScratchpad.card.creative = toCreativeNodeData(creative);

    return toolResult(`Creative "${creative.name}" (ID: ${creativeId}) set successfully.`);
  }

  return tool<typeof setCreativeInputSchema, CreativeCardScratchpadState>({
    name: V2CardAgentToolName.SetCreative,
    displayName: 'Setting creative',
    description:
      'Set the creative on the card. Validates the creative exists and is compatible with the campaign. For fallback creatives, validates the creative does not use products, location, content, or weather.',
    inputSchema: setCreativeInputSchema,
    execute,
  });
}

export default setCreativeTool;
